import supabase from './supabaseClient';

const SOURCE_APP = 'meal-manager';
const APP_LABEL = 'Meal Manager';

/** Short display id e.g. #A1B2C3D4 */
export const formatTicketNumber = (id) => {
  if (!id) return '—';
  return `#${String(id).replace(/-/g, '').slice(0, 8).toUpperCase()}`;
};

async function resolveTicketContext({ careHomeId, organizationId, careHomeName, organizationName }) {
  let chName = careHomeName || null;
  let orgName = organizationName || null;
  let orgId = organizationId || null;

  if (careHomeId && !chName) {
    const { data } = await supabase
      .from('care_homes')
      .select('name, organization_id')
      .eq('id', careHomeId)
      .maybeSingle();
    chName = data?.name || null;
    if (!orgId && data?.organization_id) orgId = data.organization_id;
  }

  if (orgId && !orgName) {
    const { data } = await supabase
      .from('organizations')
      .select('name')
      .eq('id', orgId)
      .maybeSingle();
    orgName = data?.name || null;
  }

  return {
    careHomeId: careHomeId || null,
    careHomeName: chName,
    organizationId: orgId || null,
    organizationName: orgName,
  };
}

async function logTicketEvent({ ticketId, eventType, actorId, actorName, actorRole, oldStatus, newStatus, body }) {
  const { error } = await supabase.from('support_ticket_events').insert([{
    ticket_id: ticketId,
    event_type: eventType,
    actor_id: actorId || null,
    actor_name: actorName || null,
    actor_role: actorRole || null,
    old_status: oldStatus || null,
    new_status: newStatus || null,
    body: body || null,
  }]);
  if (error) console.warn('support_ticket_events insert failed:', error.message);
}

// ─── Image upload ──────────────────────────────────────────────────────────
export async function uploadSupportImage(file, ticketId) {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${ticketId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage
    .from('support-ticket-images')
    .upload(path, file, { upsert: false, contentType: file.type });
  if (error) throw error;
  const { data: urlData } = supabase.storage.from('support-ticket-images').getPublicUrl(path);
  return urlData?.publicUrl || path;
}

// ─── Submit new ticket ─────────────────────────────────────────────────────
export async function submitSupportTicket({
  title,
  description,
  whatHappened,
  imageUrls = [],
  submittedBy,
  submitterName,
  submitterEmail,
  submitterRole,
  careHomeId,
  careHomeName,
  organizationId,
  organizationName,
}) {
  const ctx = await resolveTicketContext({
    careHomeId,
    organizationId,
    careHomeName,
    organizationName,
  });

  const { data, error } = await supabase
    .from('support_tickets')
    .insert([{
      title,
      description,
      what_happened: whatHappened,
      image_urls: imageUrls,
      submitted_by: submittedBy,
      submitter_name: submitterName,
      submitter_email: submitterEmail,
      submitter_role: submitterRole,
      care_home_id: ctx.careHomeId,
      care_home_name: ctx.careHomeName,
      organization_id: ctx.organizationId,
      organization_name: ctx.organizationName,
      source_app: SOURCE_APP,
      status: 'open',
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── Fetch all tickets (super admin) ──────────────────────────────────────
export async function fetchAllTickets({ status, sourceApp, limit = 200, offset = 0 } = {}) {
  let q = supabase
    .from('support_tickets')
    .select('*')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (sourceApp) q = q.eq('source_app', sourceApp);
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function fetchMyTickets(userId) {
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('submitted_by', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchTicket(ticketId) {
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('id', ticketId)
    .single();
  if (error) throw error;
  return data;
}

export async function fetchTicketMessages(ticketId) {
  const { data, error } = await supabase
    .from('support_ticket_messages')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchTicketEvents(ticketId) {
  const { data, error } = await supabase
    .from('support_ticket_events')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) {
    console.warn('fetchTicketEvents:', error.message);
    return [];
  }
  return data || [];
}

export async function postTicketMessage({ ticketId, senderId, senderName, senderRole, body, imageUrls = [] }) {
  const { data, error } = await supabase
    .from('support_ticket_messages')
    .insert([{
      ticket_id: ticketId,
      sender_id: senderId,
      sender_name: senderName,
      sender_role: senderRole,
      body,
      image_urls: imageUrls,
    }])
    .select()
    .single();
  if (error) throw error;

  if (senderRole === 'super_admin') {
    await logTicketEvent({
      ticketId,
      eventType: 'reply',
      actorId: senderId,
      actorName: senderName,
      actorRole: senderRole,
      body,
    });
  }

  if (senderRole === 'user') {
    await supabase
      .from('support_tickets')
      .update({ still_open: true, status: 'open' })
      .eq('id', ticketId);
  }

  return data;
}

export async function updateTicketStatus({ ticketId, status, updatedBy, updatedByName, previousStatus }) {
  const { data, error } = await supabase
    .from('support_tickets')
    .update({
      status,
      status_updated_by: updatedBy,
      status_updated_by_name: updatedByName,
      status_updated_at: new Date().toISOString(),
      still_open: false,
    })
    .eq('id', ticketId)
    .select()
    .single();
  if (error) throw error;

  await logTicketEvent({
    ticketId,
    eventType: 'status_change',
    actorId: updatedBy,
    actorName: updatedByName,
    actorRole: 'super_admin',
    oldStatus: previousStatus,
    newStatus: status,
  });

  return data;
}

export async function resolveTicket({ ticketId, resolvedBy, resolvedByName, resolutionNote, previousStatus }) {
  const { data, error } = await supabase
    .from('support_tickets')
    .update({
      status: 'resolved',
      resolved_at: new Date().toISOString(),
      resolved_by: resolvedBy,
      resolved_by_name: resolvedByName,
      resolution_note: resolutionNote || null,
      status_updated_by: resolvedBy,
      status_updated_by_name: resolvedByName,
      status_updated_at: new Date().toISOString(),
      still_open: false,
    })
    .eq('id', ticketId)
    .select()
    .single();
  if (error) throw error;

  await logTicketEvent({
    ticketId,
    eventType: 'resolved',
    actorId: resolvedBy,
    actorName: resolvedByName,
    actorRole: 'super_admin',
    oldStatus: previousStatus,
    newStatus: 'resolved',
    body: resolutionNote || null,
  });

  return data;
}

export async function submitTicketRating({ ticketId, rating, comment }) {
  const { data, error } = await supabase
    .from('support_tickets')
    .update({
      rating,
      rating_comment: comment || null,
      rated_at: new Date().toISOString(),
    })
    .eq('id', ticketId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function enrichTicketDisplay(ticket) {
  if (!ticket) return ticket;
  const ctx = await resolveTicketContext({
    careHomeId: ticket.care_home_id,
    organizationId: ticket.organization_id,
    careHomeName: ticket.care_home_name,
    organizationName: ticket.organization_name,
  });
  return {
    ...ticket,
    care_home_name: ctx.careHomeName || ticket.care_home_name,
    organization_name: ctx.organizationName || ticket.organization_name,
    organization_id: ctx.organizationId || ticket.organization_id,
  };
}

export async function notifySuperAdminsNewTicket({ ticketId }) {
  const { error } = await supabase.rpc('notify_super_admins_support_ticket', {
    p_ticket_id: ticketId,
    p_notification_type: 'support_ticket_new',
  });
  if (error) throw error;
}

export async function notifyUserTicketResolved({ ticketId }) {
  const { error } = await supabase.rpc('notify_user_support_ticket_resolved', {
    p_ticket_id: ticketId,
  });
  if (error) throw error;
}

export async function notifySuperAdminsFollowUp({ ticketId }) {
  const { error } = await supabase.rpc('notify_super_admins_support_ticket', {
    p_ticket_id: ticketId,
    p_notification_type: 'support_ticket_followup',
  });
  if (error) throw error;
}

export async function notifyUserTicketReply({ ticketId, preview }) {
  const { error } = await supabase.rpc('notify_user_support_ticket_reply', {
    p_ticket_id: ticketId,
    p_preview: preview || null,
  });
  if (error) throw error;
}

export function buildSupportTicketThread(ticket, messages = []) {
  const thread = [];
  const isCurrentlyResolved = ticket?.status === 'resolved' || ticket?.status === 'closed';

  if (ticket?.description) {
    thread.push({
      id: 'initial',
      isSupport: false,
      name: ticket.submitter_name || 'User',
      time: ticket.created_at,
      body: ticket.description,
    });
  }

  messages.forEach((msg) => {
    thread.push({
      id: msg.id,
      isSupport: msg.sender_role === 'super_admin',
      name: msg.sender_role === 'super_admin' ? (msg.sender_name || 'Support') : (msg.sender_name || 'User'),
      time: msg.created_at,
      body: msg.body,
    });
  });

  if (ticket?.resolution_note) {
    const isDuplicate = messages.some(
      (m) => m.sender_role === 'super_admin' && m.body?.trim() === ticket.resolution_note.trim(),
    );
    if (!isDuplicate) {
      thread.push({
        id: 'resolution',
        isSupport: true,
        name: ticket.resolved_by_name || 'Support',
        time: ticket.resolved_at || ticket.status_updated_at,
        body: ticket.resolution_note,
        variant: isCurrentlyResolved ? 'resolution' : 'past-resolution',
      });
    }
  }

  thread.sort((a, b) => new Date(a.time || 0) - new Date(b.time || 0));
  return thread;
}

export function wasTicketReopened(ticket) {
  if (!ticket) return false;
  const isOpen = ticket.status === 'open' || ticket.status === 'in_progress';
  return isOpen && (!!ticket.resolved_at || ticket.still_open);
}

export function subscribeToSupportTicketMessages(ticketId, onChange) {
  if (!ticketId) return null;

  return supabase
    .channel(`support-ticket-messages:${ticketId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'support_ticket_messages',
        filter: `ticket_id=eq.${ticketId}`,
      },
      (payload) => onChange?.(payload),
    )
    .subscribe();
}

export function subscribeToSupportTicket(ticketId, onChange) {
  if (!ticketId) return null;

  return supabase
    .channel(`support-ticket:${ticketId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'support_tickets',
        filter: `id=eq.${ticketId}`,
      },
      (payload) => onChange?.(payload),
    )
    .subscribe();
}

export function subscribeToSupportTickets(onChange) {
  return supabase
    .channel('support-tickets-list')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'support_tickets' },
      () => onChange?.(),
    )
    .subscribe();
}

export function subscribeToSupportTicketMessageInserts(onChange) {
  return supabase
    .channel('support-ticket-message-inserts')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'support_ticket_messages',
      },
      (payload) => onChange?.(payload),
    )
    .subscribe();
}

export { SOURCE_APP, APP_LABEL };
